import { h, InjectionKey, reactive } from 'vue';
import { simpleCopy } from '@/utils/deepCopy';
import { IRecommendOrderArrivalForRecommendEdit, IBusinessDetail, RecommendOrderArgs } from './interfaces/recommendOrder';
import { RecommendService } from '../domains/services/recommendService';
import { AnnounceService } from '../domains/services/announceService';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IRecommendOrderArrival } from '@/recommendOrderModule/domains/entities/recommendOrderArrival';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { StdIndexInfo, StdInventoryIndex } from '@/coreDomainsModule/entities/inventory';
import {
  openConfirmModal,
  openFailToAcquireModal,
  openOkInformationModal,
  openSuccessToSaveModal,
} from '@/corePagesModule/components/modal/modalActions';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { TableContentViewModel } from '@/viewModels/tableContent';
import { DateUnitType, IYearMonth } from '@/coreDomainsModule/date';
import { RemindDateInfo } from '@/remindModule/entities/reminder';
import { openRemindDateModal } from '@/remindModule/components/modal/remindDateModal';
import { saveReminderMailInfo } from '@/remindModule/remindmailManage';
import { i18n } from '@/i18n';
import { getRandomStr } from '@/businessRepository/idUtils';
import { IArrival, OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { DisplayIArrival } from './interfaces/displayIArrival';
import { BulkPostOrderResponseInfoRegisteredOrderIdsInner } from '@/coreInfrastructureModule/axios/generated/orderManage/api';
import { BusinessService } from '../domains/services/businessService';
import { diContainer } from '@/coreContainer/container';
import { updateReminder } from '@/businessRepository/reminderTable/driver';
import { ReminderMailInfo } from '@/remindModule/entities/reminder';
import { ArrivalFactory } from '@/coreDomainsModule/factories/ArrivalFactory';
import { initialAggregateBusinessFilterProperty } from '@/coreDomainsModule/entities/filter';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import OrderGroupInfoSection from '@/corePagesModule/components/OrderGroup/OrderGroupInfoSection.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { loadMyPropertyPreferences, myPropertyPreferences } from '@/appLogic/view/preferences';
import { IOrderBasisAdjustment } from '@/coreDomainsModule/entities/orderBasis';
import { ForecastService } from '@/coreDomainsModule/services/forecastService';

/**
 * 取引情報をシステムから取得する
 * @param args - {RecommendOrderArgs}
 * @returns `
 */
export const useRecommendOrderStore = (args: RecommendOrderArgs): IRecommendOrderStore => {
  const state = reactive<IBusinessDetail>({
    formSet: BusinessFactory.create(),
    customerForecastHistory: [],
    loadingState: {
      status: {
        type: 'none',
      },
    },
    selectedInfo: initialAggregateBusinessFilterProperty,
    selectedInitialInfo: initialAggregateBusinessFilterProperty,
    recommend: [],
    partialDeliveryForms: [],
    supplierAnnounce: [],
    progress: 'EditReceiveAnnounce',
    renderRecommendEditKey: 0,
    renderOrderFormKey: 0,
    announceAdjustmentRate: 1,
    defaultCustomerAnnounce: [],
    checkedIndex: [],
    recommendEditDefinedOrderScrollPosition: {
      x: 0,
      y: 0,
    },
    recommendEditDefinedOrderResizeState: {
      height: undefined,
      minHeight: undefined,
      hasUserResized: false,
    },
    unpaidOrderedOrder: [],
    isOpeningEditUnpaidOrderedOrderModal: false,
    relatedTransactionsModalState: false,
    relatedTransactionsData: null,
    relatedTransactionsLoading: false,
    relatedTransactionsError: null,
    relatedTransactionsReceiptMonthOffset: 1,
    longTermCustomerForecastData: undefined,
    futureMonthlyCalculations: [],
  });

  let bizId = args.businessId;
  const vm = new TableContentViewModel();
  const t = i18n.t;

  /**
   * bizIdを更新する
   * @param businessId - 取引ID
   */
  const updateBizId = (businessId: string) => {
    bizId = businessId;
  };

  /**
   * 内示調整の倍率を更新する
   * @param rate - 内示調整の倍率
   */
  const updateAnnounceAdjustmentRate = (rate: number) => {
    state.announceAdjustmentRate = rate;
  };

  /**
   * 初期化する
   */
  const resetSimulationInfo = () => {
    state.extraSimulateInfos = {
      extraAnnounce: simpleCopy(state.formSet.customerAnnounce),
      extraRecommend: simpleCopy(state.formSet.recommend),
      extraStdInventory: simpleCopy(state.formSet.stdInventoryIndices?.['標準在庫指数']),
      extraLeadTime: state.formSet.leadTime,
      extraStartDate: undefined,
      isFirstSimulate: true,
      isResetSimulate: false,
    };
    state.selectedLeadTimeDisplayValue = state.formSet.leadTime;
    state.selectedLeadTimeUnit = state.formSet.leadTimeUnit;
    state.formSet?.recommend?.forEach((item) => {
      state.recommend.push({
        displayId: getRandomStr(),
        ...item,
        isLocked: item.isFixOrdered ? true : false,
        isChecked: false,
        isExpanded: false,
      });
    });
    state.partialDeliveryForms = [];
    state.errorType = undefined;
  };

  /**
   * 全てのstateを初期化する
   */
  const resetAllSimulateState = async () => {
    // 集計を再取得する
    const refreshItem = await diContainer.getBusinessUseCase.Execute(bizId as string);
    switch (refreshItem.status.type) {
      case 'success':
        // 内示情報を過去表示用のstate変数に保存
        state.customerForecastHistory = simpleCopy(refreshItem.status.data.customerAnnounce ?? []);

        // 取引情報の取得
        state.formSet = BusinessService.convertRecommendEditBusiness(refreshItem.status.data);

        // フィルターを埋める
        updateSelectedInfo(
          state.formSet.warehouse,
          state.formSet.supplierName,
          state.formSet.businessNumber,
          state.formSet.businessName,
          state.formSet.package
        );

        // フィルターの初期値を設定
        state.selectedInitialInfo = simpleCopy(state.selectedInfo);

        // 発注済み未納を取得
        state.unpaidOrderedOrder = BusinessService.getUnpaidOrderedOrder(refreshItem.status.data);
    }

    state.recommend = [];
    state.supplierAnnounce = [];
    state.progress = 'EditReceiveAnnounce';
    state.renderRecommendEditKey = 0;
    state.renderOrderFormKey = 0;
    state.announceAdjustmentRate = 1;
    state.selectedLeadTimeDisplayValue = undefined;
    state.selectedLeadTimeUnit = undefined;
    state.checkedIndex = [];
    state.recommendEditDefinedOrderScrollPosition = {
      x: 0,
      y: 0,
    };
    state.isOpeningEditUnpaidOrderedOrderModal = false;
    resetSimulationInfo();

    // 確定発注が存在する場合、最終確定日の翌日の日付を取得し、extraSimulateを更新する
    const lockedRecommend = state.recommend.filter((item) => item.isLocked || item.arrivalId != '') ?? [];
    const lockedRecommendLast = lockedRecommend?.[lockedRecommend?.length - 1];

    const startDate = calculateStartDate(lockedRecommendLast);
    if (startDate) {
      updateExtraSimulate(undefined, undefined, undefined, lockedRecommend, startDate);
    }
  };

  /**
   * 編集中の標準在庫指数、リードタイムシミュレーション値を残したまま発注を初期化する
   */
  const resetOnlyRecommendOrder = () => {
    state.recommend = [];
    state.supplierAnnounce = [];
    state.renderRecommendEditKey += 1;
    state.renderOrderFormKey += 1;
    state.checkedIndex = [];
    state.recommendEditDefinedOrderScrollPosition = {
      x: 0,
      y: 0,
    };
    if (state.extraSimulateInfos) {
      state.extraSimulateInfos.extraRecommend = simpleCopy(state.formSet.recommend);
      // すでに消えた確定発注の日付が残っている可能性があるため、一度undefinedにしてもし確定発注が初期化時にあれば下でupdateする
      state.extraSimulateInfos.extraStartDate = undefined;
      // isFirstSimulateをTrueにしてシミュレートを回す
      state.extraSimulateInfos.isFirstSimulate = true;
      state.extraSimulateInfos.isResetSimulate = false;
    }

    state.formSet?.recommend?.forEach((item) => {
      state.recommend.push({
        displayId: getRandomStr(),
        ...item,
        isLocked: item.isFixOrdered ? true : false,
        isChecked: false,
        isExpanded: false,
      });
    });
    state.partialDeliveryForms = [];
    state.errorType = undefined;
    const lockedRecommend = state.recommend.filter((item) => item.isLocked || item.arrivalId != '') ?? [];
    const lockedRecommendLast = lockedRecommend?.[lockedRecommend?.length - 1];

    const startDate = calculateStartDate(lockedRecommendLast);
    if (startDate) {
      updateExtraSimulate(undefined, undefined, undefined, lockedRecommend, startDate);
    }
  };

  /**
   * IBusinessDetailを初期化する
   */
  const _initUiState = () => {
    state.formSet = BusinessFactory.create();
    state.loadingState = {
      status: {
        type: 'none',
      },
    };
    state.selectedInfo = initialAggregateBusinessFilterProperty;
    state.isOpenFilterType = undefined;
    state.progress = 'EditReceiveAnnounce';
    state.recommend = [];
    state.partialDeliveryForms = [];
    state.renderRecommendEditKey = 0;
    state.renderOrderFormKey = 0;
    state.announceAdjustmentRate = 1;
    state.supplierAnnounce = [];
    state.extraSimulateInfos = {};
    state.selectedLeadTimeDisplayValue = undefined;
    state.selectedLeadTimeUnit = undefined;
    state.defaultCustomerAnnounce = [];
    state.checkedIndex = [];
    state.errorType = undefined;
    state.recommendEditDefinedOrderScrollPosition = {
      x: 0,
      y: 0,
    };
    state.recommendEditDefinedOrderResizeState = {
      height: undefined,
      minHeight: undefined,
      hasUserResized: false,
    };
    state.unpaidOrderedOrder = [];
    state.isOpeningEditUnpaidOrderedOrderModal = false;
    state.longTermCustomerForecastData = undefined;
    state.futureMonthlyCalculations = [];
  };

  /**
   * formSetとrecommendの差分を吸収しつつ、recommendを更新する
   * @param recommend - 発注情報？？？
   */
  const updateSimulate = (recommend: IRecommendOrderArrival[]) => {
    // 鍵がかかっている or 確定発注のもの
    const lockedRecommend = state.recommend.filter((item) => item.isLocked || item.isFixOrdered);
    const output: IRecommendOrderArrivalForRecommendEdit[] = [];
    state.recommend = recommend.map((item) => {
      return { ...item, partialDeliveries: [], isLocked: false, isChecked: false, isExpanded: false };
    });
    // ロックされている要素を元に戻す
    lockedRecommend.forEach((item) => {
      output.push(item);
    });
    recommend.forEach((item) => {
      // outputにないものは追加する
      const index = output.findIndex((it) => {
        const crossDate = it.crossDate;
        const itemCrossDate = item.crossDate;
        if (crossDate === undefined || itemCrossDate === undefined) return false;
        return DateService.equalYearMonthDayInfo(crossDate, itemCrossDate);
      });
      if (index === -1) {
        const pushObject: IRecommendOrderArrivalForRecommendEdit = {
          ...item,
          partialDeliveries: [],
          isLocked: false,
          isChecked: false,
          isExpanded: false,
        };

        // displayIdがついていなかったらここで付与してあげる
        pushObject.displayId = pushObject.displayId == null || pushObject.displayId === '' ? getRandomStr() : pushObject.displayId;
        output.push(pushObject);
      }
    });

    state.recommend = RecommendService.sortRecommend(output);
    state.checkedIndex = state.recommend.reduce((acc, recommend, index) => (recommend.isChecked ? [...acc, index] : acc), [] as number[]);
    state.renderRecommendEditKey += 1;
  };

  /**
   * OrderForm -> RecommendEditへのデータの受け渡し
   * @param recommend - 発注推奨
   * @param customerAnnounce - 客先内示
   */
  const updateRecommend = (recommend: IRecommendOrderArrivalForRecommendEdit[], customerAnnounce: IAnnounce[]) => {
    state.recommend = simpleCopy(recommend);
    state.extraSimulateInfos = {};
    state.extraSimulateInfos.extraRecommend = simpleCopy(recommend);
    state.extraSimulateInfos.extraAnnounce = simpleCopy(customerAnnounce);
    // 余計なプロパティを削る
    state.extraSimulateInfos.extraRecommend = state.extraSimulateInfos.extraRecommend.map((item) => {
      delete item['isLocked'];
      return item;
    });
  };

  /**
   * RecommendEdit -> OrderFormへのデータの受け渡し
   * @param customerAnnounce - 客先内示
   * @param stdInventoryIndices - 標準在庫指数
   * @param leadTime - リードタイム
   * @param recommend - 発注推奨
   * @param startDate - 開始日
   */
  const updateExtraSimulate = (
    customerAnnounce?: IAnnounce[],
    stdInventoryIndices?: StdInventoryIndex,
    leadTime?: number,
    recommend?: IRecommendOrderArrival[],
    startDate?: Date
  ) => {
    // 引数のうち、１つでもundefined以外がある場合に初期化
    if (!customerAnnounce && !stdInventoryIndices && !leadTime && !recommend && !startDate) return;
    state.extraSimulateInfos = {
      extraAnnounce: state.extraSimulateInfos?.extraAnnounce,
      extraRecommend: state.extraSimulateInfos?.extraRecommend,
      extraStdInventory: state.extraSimulateInfos?.extraStdInventory,
      extraLeadTime: state.extraSimulateInfos?.extraLeadTime,
      extraStartDate: state.extraSimulateInfos?.extraStartDate,
    };

    if (customerAnnounce && state.extraSimulateInfos.extraAnnounce) {
      state.extraSimulateInfos.extraAnnounce = AnnounceService.unionCustomerAnnounce(
        state.extraSimulateInfos.extraAnnounce,
        customerAnnounce
      );
    }
    if (stdInventoryIndices) {
      state.extraSimulateInfos.extraStdInventory = simpleCopy(stdInventoryIndices);
    }
    if (leadTime) {
      state.extraSimulateInfos.extraLeadTime = leadTime;
    }

    // シミュレーションやり直しに対応
    if (recommend && !state.extraSimulateInfos.isResetSimulate) {
      state.extraSimulateInfos.extraRecommend = simpleCopy(recommend);
    } else {
      state.extraSimulateInfos.extraRecommend = undefined;
      state.extraSimulateInfos.isResetSimulate = false;
    }

    if (startDate) {
      state.extraSimulateInfos.extraStartDate = startDate;
    } else {
      state.extraSimulateInfos.extraStartDate = undefined;
    }
    if (state.extraSimulateInfos) {
      state.extraSimulateInfos.isFirstSimulate = false;
    }
    state.extraSimulateInfos = simpleCopy(state.extraSimulateInfos);
    state.renderOrderFormKey += 1;
  };

  /**
   * RecommendEdit -> OrderFormへのデータの受け渡し(recommendのみ)
   * @param recommend - 発注推奨
   */
  const updateExtraRecommend = (recommend?: IRecommendOrderArrivalForRecommendEdit[]) => {
    if (!recommend) return;
    state.recommend = simpleCopy(recommend);
  };

  /**
   * RecommendEdit -> OrderFormへのデータの受け渡し(仕入先内示のみ)
   * @param supplierAnnounce - 仕入先内示
   */
  const updateExtraSupplierAnnounce = (supplierAnnounce?: IAnnounce[]) => {
    if (!supplierAnnounce) return;
    state.supplierAnnounce = simpleCopy(supplierAnnounce);
  };

  /**
   * 発注推奨にロックをかける
   * @param index - ロックするrecommendのindex
   */
  const lockRecommend = (index: number) => {
    // 0~indexまでロックする
    for (let i = 0; i <= index; i++) {
      state.recommend[i].isLocked = true;
    }
  };

  /**
   * 発注推奨のロックを解除する
   * @param index - ロックを解除するrecommendのindex
   */
  const unlockRecommend = (index: number) => {
    state.recommend[index].isLocked = false;
    state.renderRecommendEditKey += 1;
  };

  /**
   * 発注推奨を追加する
   * @param recommend - 追加するrecommend
   */
  const addRecommend = (recommend?: IRecommendOrderArrivalForRecommendEdit) => {
    const newRecommend: IRecommendOrderArrivalForRecommendEdit = recommend
      ? recommend
      : {
          displayId: getRandomStr(),
          year: -1,
          month: -1,
          day: -1,
          arrivalId: '',
          amount: 0,
          leadTime: state.extraSimulateInfos?.extraLeadTime ?? 0,
          partialDeliveries: [],
          isLocked: false,
          isChecked: false,
          isExpanded: false,
          isFixOrdered: undefined,
          crossDate: undefined,
          fixOrderDate: undefined,
          status: undefined,
          memoText: undefined,
          number: undefined,
          updateAt: undefined,
        };
    state.recommend.push(newRecommend);
    return newRecommend;
  };

  /**
   * 発注推奨を削除する
   * @param index - 削除するrecommendのindex
   */
  const trashRecommend = (index: number) => {
    state.recommend.splice(index, 1);
    state.renderRecommendEditKey += 1;
  };

  /**
   * 客先内示を確定する
   */
  const confirmReceiveAnnounce = () => {
    state.progress = 'EditRecommend';
    if (state.extraSimulateInfos) {
      state.extraSimulateInfos.isFirstSimulate = true;
    }
  };

  /**
   * 客先内示を修正する
   */
  const reviseCustomerAnnounce = () => {
    state.progress = 'EditReceiveAnnounce';
    if (state.extraSimulateInfos) {
      state.extraSimulateInfos.isResetSimulate = true;
    }
  };

  /**
   * 発注推奨を確定する
   * @param checkedRecommends - 確定する発注推奨の配列
   */
  const confirmRecommend = async (checkedRecommends: IRecommendOrderArrivalForRecommendEdit[]): Promise<INetworkResult<void, Error>> => {
    // 発注済み未納のバリデーション
    await loadMyPropertyPreferences();
    if (myPropertyPreferences.order.unpaidOrderedOrderAppearance && state.unpaidOrderedOrder.length > 0) {
      const confirmHasUnpaidOrderedOrder = await openUnpaidOrderedOrderInfoModal(true);
      if (!confirmHasUnpaidOrderedOrder) {
        return {
          status: {
            type: 'error',
            error: new Error('発注確定に失敗しました'),
          },
        };
      }
    }
    // 発注グループ周りのバリデーション
    const belongOrderGroupRecommends = checkedRecommends.filter((recommend) => recommend.orderGroupId);
    if (belongOrderGroupRecommends.length && !(await waitLoading(_checkUpperLimitQuantityForOrderGroup(checkedRecommends)))) {
      return {
        status: {
          type: 'error',
          error: new Error('発注確定に失敗しました'),
        },
      };
    }
    state.progress = 'EditSupplierAnnounce';
    return saveConfirmOrderInfo(checkedRecommends);
  };

  /**
   * 発注済み未納に関するInfoModalを開く
   * @param isProcessingConfirm - 発注推奨確定中かどうか
   */
  const openUnpaidOrderedOrderInfoModal = async (isProcessingConfirm = false) => {
    let isCanceled = false;
    const modalDescription = `${t('最新の在庫に反映されていない場合、誤った推奨発注を表示している場合があります。')}${
      isProcessingConfirm ? t('続行しますか？') : ''
    }`;
    const unpaidOrderedOrderInfoModalResult = await showInfoModal({
      modalTitle: t('発注済み未納が存在します'),
      modalDescription: modalDescription,
      modalFooter: isProcessingConfirm
        ? ({ onBack }) =>
            h(
              'button',
              {
                style: {
                  color: '#1e92ef',
                  borderBottom: '1px solid',
                },
                // eslint-disable-next-line jsdoc/require-jsdoc
                onClick: () => {
                  isCanceled = true;
                  onBack();
                },
              },
              t('キャンセル')
            )
        : undefined,
      backButtonLabel: isProcessingConfirm ? t('続行する') : t('キャンセル'),
      nextButtonLabel: t('発注済み未納を確認する'),
      icon: 'info',
      colorStyle: 'primary',
      enableOutsideClick: false,
      modalWidth: 764,
    });

    if (unpaidOrderedOrderInfoModalResult) {
      // 発注済み未納確認
      // モーダル展開
      state.isOpeningEditUnpaidOrderedOrderModal = true;
      return false;
    } else if (isProcessingConfirm && !isCanceled) {
      // 発注推奨確定時でモーダル続行押下
      return true;
    } else {
      // キャンセル
      return false;
    }
  };

  /**
   * 発注推奨（すでに承認済みの発注）の中に発注グループに所属しているものがあった場合、その上限数量が超過していないかをチェックする関数
   * @param checkedRecommends - 確定する発注推奨の配列
   * @returns - True: バリデーション通過, False: バリデーションに引っかかっている
   */
  const _checkUpperLimitQuantityForOrderGroup = async (checkedRecommends: IRecommendOrderArrivalForRecommendEdit[]): Promise<boolean> => {
    // ローディングモーダルが一瞬で消えると画面がチカチカしてUX悪いのでそれを防ぐために0.5秒ディレイを入れる
    setTimeout(() => {}, 500);
    const belongOrderGroupRecommends = checkedRecommends.filter((recommend) => recommend.orderGroupId);
    // 発注グループの情報を取得
    const getOrderGroupInfoResult = await diContainer.getOrderGroupUseCase.execute();
    if (getOrderGroupInfoResult.status.type === 'error') {
      console.error('発注グループの取得に失敗');
      await openOkInformationModal('error', t('登録に失敗しました'), t('閉じる'), undefined, t('発注確定エラーテキスト'));
      return false;
    }
    const orderGroupInfos = getOrderGroupInfoResult.status.data;
    // 元々の発注数量を持っておく
    const originalOrderGroupAmounts = orderGroupInfos.reduce((acc, orderGroup) => {
      acc[orderGroup.orderGroupId] = orderGroup.totalLinkedOrderAmount;
      return acc;
    }, {});
    // 発注グループの合計数量を編集後の数量で更新する
    for (const recommend of belongOrderGroupRecommends) {
      const originalRecommend = state.formSet.recommend.find((r) => r.arrivalId == recommend.arrivalId);
      const targetOrderGroupInfo = orderGroupInfos.find((orderGroup) => orderGroup.orderGroupId === recommend.orderGroupId);
      if (!originalRecommend || !targetOrderGroupInfo) {
        console.error('オリジナルの情報がないか対象の発注グループがない');
        await openOkInformationModal('error', t('登録に失敗しました'), t('閉じる'), undefined, t('発注確定エラーテキスト'));
        return false;
      }
      const diffAmount = recommend.amount - originalRecommend.amount;
      targetOrderGroupInfo.totalLinkedOrderAmount += diffAmount;
    }
    // 編集後の合計数量が上限数量を上回っていないかチェックする
    const overUpperLimitQuantityOrderGroup = orderGroupInfos.filter(
      (orderGroup) => orderGroup.totalLinkedOrderAmount > orderGroup.upperLimitQuantity
    );
    if (overUpperLimitQuantityOrderGroup.length) {
      console.error('上限数量オーバー');
      await showInfoModal({
        modalTitle: t('発注グループの上限数量オーバー'),
        modalDescription: t('発注グループの上限数量の引き上げ、または各発注の数量を上限数量に収まるようにしてください。'),
        modalContent: h(
          'div',
          null,
          overUpperLimitQuantityOrderGroup.map((orderGroup) =>
            h(OrderGroupInfoSection, {
              orderGroupName: orderGroup.orderGroupName,
              numberOfOrder: orderGroup.linkedOrderCount,
              upperLimitQuantity: orderGroup.upperLimitQuantity,
              totalAmount: originalOrderGroupAmounts[orderGroup.orderGroupId],
              afterTotalAmount: orderGroup.totalLinkedOrderAmount,
              hasBackground: true,
            })
          )
        ),
        backButtonLabel: 'OK',
        icon: 'error',
        colorStyle: 'error',
        enableOutsideClick: false,
        isDisabledNextButton: true,
        modalWidth: 764,
      });

      return false;
    }
    return true;
  };

  /**
   * 発注推奨を修正する
   */
  const reviseRecommend = () => {
    state.progress = 'EditRecommend';
  };

  /**
   * 仕入先内示を確定する
   * @param registerSupplierForecastRange - 登録する仕入先内示の期間
   * @param useCustomerAnnounce - 客先内示を仕入先内示とするか
   * @param selectedYearMonth - 選択年月
   */
  const confirmSupplierAnnounce = (registerSupplierForecastRange: number, useCustomerAnnounce: boolean, selectedYearMonth?: IYearMonth) => {
    saveConfirmSupplierAnnounce(registerSupplierForecastRange, useCustomerAnnounce, selectedYearMonth);
  };

  /**
   * 発注推奨シミュレーションをリセットする
   */
  const resetExtraSimulation = () => {
    resetSimulationInfo();
    state.recommend = [];
  };

  /**
   * 確定発注を保存する
   * @param checkedRecommends - 確定発注フラグを更新する配列
   */
  const saveConfirmOrderInfo = async (
    checkedRecommends: IRecommendOrderArrivalForRecommendEdit[]
  ): Promise<INetworkResult<void, Error>> => {
    // モーダルで確認モーダルを作成
    // OKの時は以降の処理を実行
    // キャンセルの時は以降の処理をスキップ
    const errorResult: INetworkResult<void, Error> = {
      status: {
        type: 'error',
        error: new Error('発注確定に失敗しました'),
      },
    };

    // 選択されている発注がない場合は登録処理を飛ばす
    if (!checkedRecommends.length) return errorResult;

    // モーダル確認モーダルを表示
    const result = await openConfirmModal(
      'question',
      t('発注の確定(詳細ページ)'),
      t('確定'),
      t('キャンセル'),
      undefined,
      t('発注確定テキスト'),
      {
        useKeyboard: true,
      }
    );
    if (!result.isConfirmed) return errorResult;

    // TODO: これ何？
    if (Array.isArray(bizId))
      // bizIdが配列の場合はスキップ
      return errorResult;

    // ここまで来たら登録処理が走るので、ローディング開始
    state.loadingState.status.type = 'loading';

    const includeRecommendedStatus: boolean = RecommendService.findRecommendedOrderFromIRecommendOrderArrivalForRecommendEdit(
      simpleCopy(checkedRecommends)
    );

    if (includeRecommendedStatus) {
      state.loadingState.status.type = 'error';
      await openOkInformationModal(
        'error',
        t('発注登録ステータスエラータイトル'),
        t('閉じる'),
        undefined,
        t('発注登録ステータスエラーテキスト')
      );
      const result = errorResult;
      result.status = {
        type: 'error',
        error: new Error('発注登録ステータスエラー'),
      };
      return result;
    }

    // recommendの情報群から、UIで選択した発注情報のみを取り出す
    const saveRecommend: IRecommendOrderArrival[] = RecommendService.setDefinedOrderFromIRecommendOrderArrivalForRecommendEdit(
      simpleCopy(checkedRecommends)
    );

    // IArrivalの情報を作成する
    const createdArrivalInfo: DisplayIArrival[] = saveRecommend.reduce<DisplayIArrival[]>((acc, item) => {
      const orderInfo = ArrivalFactory.create(item);
      acc.push({
        ...orderInfo,
        displayId: item.displayId, // 更新が成功した時にorderIdを更新するために必要
      });
      return acc;
    }, []);

    const adjustment: IOrderBasisAdjustment = {
      leadTime:
        state.selectedLeadTimeDisplayValue && state.selectedLeadTimeUnit
          ? { value: state.extraSimulateInfos?.extraLeadTime ?? state.selectedLeadTimeDisplayValue, unit: state.selectedLeadTimeUnit }
          : undefined,
      stdInventoryIndex: state.extraSimulateInfos?.extraStdInventory,
      customerForecasts: ForecastService.extractLatestReceivedCustomerForecasts(state.extraSimulateInfos?.extraAnnounce ?? []),
    };

    // 発注情報の登録
    const saveResult = await diContainer.upsertOrderUseCase.Execute(createdArrivalInfo, bizId, adjustment);
    switch (saveResult.status.type) {
      case 'success':
        // 完了モーダルを表示する
        state.loadingState.status.type = 'success';
        await openSuccessToSaveModal({ useKeyboard: true });

        // orderIdとisFixOrderedを更新する
        _updateOrderIdForRegisteredOrder(saveResult.status.data.registeredOrderIds);
        // 更新した発注情報をstate.formSetに反映する (これをしないと初期化時に登録済み発注情報が反映されない)
        state.formSet.recommend = simpleCopy(state.recommend);
        break;

      case 'error':
        // エラーモーダルを表示する
        console.error('saveConfirmRecommendAndSupplierAnnounce1 error');
        state.loadingState.status.type = 'error';
        if (saveResult.status.error.code === 'UPDATE_TIMING_CONFLICT') {
          await openOkInformationModal(
            'error',
            t('登録に失敗しました'),
            t('閉じる'),
            undefined,
            t('発注の更新時に他のユーザーと競合しました。リロードして再度お試しください。'),
            { useKeyboard: true }
          );
        } else {
          await openOkInformationModal('error', t('登録に失敗しました'), t('閉じる'), undefined, t('発注確定エラーテキスト'), {
            useKeyboard: true,
          });
        }
        return errorResult;
    }

    // リマインド設定（設定がONの場合のみ表示）
    await loadMyPropertyPreferences();
    if (myPropertyPreferences.order.orderReminderAppearance) {
      // TODO: 発注登録後、取引情報取得処理を行うことで、モーダル出現までのラグがあるので対策する
      const fetchResult2 = await diContainer.getBusinessUseCase.Execute(bizId);
      state.loadingState.status.type = 'loading';
      switch (fetchResult2.status.type) {
        case 'success':
          state.loadingState.status.type = 'success';
          const getBusiness: IBusiness = fetchResult2.status.data;

          // TODO: 失敗とかの切り分け
          // datepickerの選択スタート位置の設定(翌日以降に設定)
          const today: Date = new Date();
          today.setDate(today.getDate() + 1);
          const defaultDate: Date = new Date(today.getFullYear(), today.getMonth(), today.getDate()); // デフォルトは翌日の日付を持つ

          // リマインド設定モーダルの表示
          // TODO: 確認モーダル周りはデザイン未確定のため、モーダルで簡易的に実装、後で変更する
          const addRemindResult: RemindDateInfo = await openRemindDateModal(defaultDate);

          // TODO: ローカルDB環境でリマインドメール登録実行するとエラーでるので、何か考える
          if (addRemindResult.result.isConfirmed) saveReminderMailInfo(addRemindResult.date, getBusiness);
          break;

        case 'error':
          // モーダルでエラーモーダルを作成
          console.log('saveConfirmRecommendAndSupplierAnnounce2 error');
          await openFailToAcquireModal();
          return errorResult;
      }
    }

    return {
      status: {
        type: 'success',
        data: undefined,
      },
    };
  };

  /**
   * 提示内示を保存する
   * @param registerSupplierForecastRange - 登録する仕入先内示の期間
   * @param useCustomerAnnounce - 客先内示と同じ値が仕入先内示に入るかを判別するフラグ
   * @param selectedYearMonth - YearMonthPickerで選択した年月情報
   */
  const saveConfirmSupplierAnnounce = async (
    registerSupplierForecastRange: number,
    useCustomerAnnounce: boolean,
    selectedYearMonth?: IYearMonth
  ) => {
    // モーダルで確認モーダルを作成
    // OKの時は以降の処理を実行
    // キャンセルの時は以降の処理をスキップ

    // 年月が選択されていなかったらエラーモーダルを出す
    if (!selectedYearMonth) {
      await openOkInformationModal('error', t('登録に失敗しました'), t('閉じる'), t('作成年月を選択してください'));
      return;
    }

    const result = await openConfirmModal(
      'question',
      t('仕入先内示の確定(詳細ページ)'),
      t('確定'),
      t('キャンセル'),
      undefined,
      t('仕入先内示確定テキスト')
    ); // モーダル確認モーダルを表示
    if (!result.isConfirmed) return;

    if (Array.isArray(bizId))
      // bizIdが配列の場合はスキップ
      return;

    if (!state.supplierAnnounce) {
      // 仕入先内示情報が欠落しているの場合
      await openOkInformationModal('error', t('エラー'), t('戻る'), t('仕入先内示確定エラーテキスト1'));
      return;
    }

    // 仕入先内示作成年月は、YearMonthPickerで指定した月-1の値が入る
    const createdYearMonth = DateService.getShiftedIYearMonthByMonths(selectedYearMonth.year, selectedYearMonth.month, -1);

    // 詳細ページに表示されている取引情報の取得
    state.loadingState.status.type = 'loading';
    const fetchResult = await diContainer.getBusinessUseCase.Execute(bizId);
    switch (fetchResult.status.type) {
      case 'success':
        // 登録する内示情報を入れる
        const registerSupplierForecastList: IAnnounce[] = simpleCopy(state.supplierAnnounce);
        // 仕入先内示の登録を行う
        const saveResult = await diContainer.putSupplierForecastUseCase.Execute(
          registerSupplierForecastList,
          useCustomerAnnounce,
          createdYearMonth,
          registerSupplierForecastRange,
          bizId
        );

        // 仕入先内示登録側のswitch-case
        switch (saveResult.status.type) {
          case 'success':
            state.loadingState.status.type = 'success';
            // 詳細画面の編集状態を完了状態にする
            state.progress = 'EditCompleted';
            await openSuccessToSaveModal();
            break;

          case 'error':
            // ここでやる？
            // 重複エラーの場合の処理
            const errorResult = saveResult.status.error;
            if (errorResult['code'] === 'RESOURCE_CONFLICT') {
              state.loadingState.status.type = 'success';
              await upsertConfirmSupplierAnnounce(createdYearMonth, registerSupplierForecastRange, useCustomerAnnounce);
              break;
            }
            // モーダルでエラーモーダルを作成
            console.error('saveConfirmRecommendAndSupplierAnnounce1 error');
            state.loadingState.status.type = 'error';
            await openOkInformationModal('error', t('登録に失敗しました'), t('確認'), t('仕入先内示確定エラーテキスト2'));
            break;
        }

        break;
      case 'error':
        // モーダルでエラーモーダルを作成
        console.error('取引情報の取得に失敗しました');
        state.loadingState.status.type = 'error';
        await openOkInformationModal('error', t('登録に失敗しました'), t('確認'), t('仕入先内示確定エラーテキスト2'));
        break;
    }
  };

  /**
   * 提示内示を更新・保存する
   * @param createdYearMonth - 仕入先内示作成年月
   * @param registerSupplierForecastRange - 登録する仕入先内示の期間
   * @param useCustomerAnnounce - 客先内示と同じ値が仕入先内示に入るかを判別するフラグ
   */
  const upsertConfirmSupplierAnnounce = async (
    createdYearMonth: IYearMonth,
    registerSupplierForecastRange: number,
    useCustomerAnnounce: boolean
  ) => {
    // モーダルで確認モーダルを作成
    // OKの時は以降の処理を実行
    // キャンセルの時は以降の処理をスキップ

    // upsert処理を実行するかどうかのフラグ
    let userUpsert = false;

    const result = await openConfirmModal(
      'warning',
      t('仕入先内示の上書き(詳細ページ)'),
      t('上書き保存'),
      t('キャンセル'),
      undefined,
      t('仕入先内示更新テキスト')
    ); // モーダル確認モーダルを表示
    if (!result.isConfirmed) return;

    // 更新フラグの状態変更
    userUpsert = result.isConfirmed;

    if (Array.isArray(bizId))
      // bizIdが配列の場合はスキップ
      return;

    if (!state.supplierAnnounce) {
      // 仕入先内示情報が欠落しているの場合
      await openOkInformationModal('error', t('エラー'), t('戻る'), t('仕入先内示確定エラーテキスト1'));
      return;
    }

    // 詳細ページに表示されている取引情報の取得
    state.loadingState.status.type = 'loading';
    const fetchResult = await diContainer.getBusinessUseCase.Execute(bizId);
    switch (fetchResult.status.type) {
      case 'success':
        // 登録する内示情報を入れる
        const registerSupplierForecastList: IAnnounce[] = simpleCopy(state.supplierAnnounce);
        // 仕入先内示の登録を行う
        const saveResult = await diContainer.putSupplierForecastUseCase.Execute(
          registerSupplierForecastList,
          useCustomerAnnounce,
          createdYearMonth,
          registerSupplierForecastRange,
          bizId,
          userUpsert
        );

        // 仕入先内示登録側のswitch-case
        switch (saveResult.status.type) {
          case 'success':
            state.loadingState.status.type = 'success';
            // 詳細画面の編集状態を完了状態にする
            state.progress = 'EditCompleted';
            await openSuccessToSaveModal();
            break;

          case 'error':
            // モーダルでエラーモーダルを作成
            console.error('saveConfirmRecommendAndSupplierAnnounce1 error');
            state.loadingState.status.type = 'error';
            await openOkInformationModal('error', t('登録に失敗しました'), t('閉じる'), t('仕入先内示確定エラーテキスト2'));
            return;
        }

        break;
      case 'error':
        // モーダルでエラーモーダルを作成
        console.error('取引情報の取得に失敗しました');
        state.loadingState.status.type = 'error';
        await openOkInformationModal('error', t('登録に失敗しました'), t('閉じる'), t('仕入先内示確定エラーテキスト2'));
        break;
    }
  };

  /**
   * 内示調整を行う
   * @param announces - 内示情報
   */
  const calcAnnounceAdjustment = (announces: IAnnounce[]) => {
    announces.forEach((announce) => {
      announce.amount = Math.round(announce.amount * state.announceAdjustmentRate);
    });
    // 一度調整したら、調整率をリセットする
    state.announceAdjustmentRate = 1;
  };

  /**
   * 推奨発注日を取得する
   * @param index - 推奨発注のインデックス
   * @returns 推奨発注日
   */
  const getRecommendOrderDate = (index: number) => {
    if (state.recommend.length === 0) return;
    return vm.getRecommendOrderDate(state.recommend[index]);
  };

  /**
   * 推奨納入日を取得する
   * @param index - 推奨発注のインデックス
   * @returns 推奨納入日
   */
  const getRecommendCrossDate = (index: number) => {
    if (state.recommend.length === 0) return;
    const recommend = state.recommend?.[index];
    if (!recommend) return;
    return vm.getRecommendCrossDate(recommend);
  };

  /**
   * 分納情報の推奨納入日を取得する
   * @param recommendIndex - 推奨発注のインデックス
   * @param partialDeliveryIndex - 分納のインデックス
   * @returns 分納情報の推奨納入日
   */
  const getPartialDeliveryCrossDate = (recommendIndex: number, partialDeliveryIndex: number) => {
    if (state.recommend.length === 0) return;
    const recommend = state.recommend?.[recommendIndex];
    if (!recommend) return;
    if (!recommend.partialDeliveries || recommend.partialDeliveries.length === 0) return;
    const partialDelivery = recommend.partialDeliveries?.[partialDeliveryIndex];
    if (!partialDelivery) return;
    // 分納の推奨納入日を取得
    return vm.getRecommendCrossDate(partialDelivery);
  };

  /**
   * 推奨発注のメモの表示用の文字列を取得する
   * @param index - 推奨発注のインデックス
   */
  const getRecommendOrderNote = (index: number) => {
    if (state.recommend.length === 0) return;
    const recommend = state.recommend?.[index];
    if (!recommend) return;
    return vm.getDisplayOrderNote(recommend);
  };

  /**
   * 推奨発注日のカレンダーで選択できる日付かどうかを判定する関数を取得する
   * @param index - 判定対象の発注推奨のインデックス
   * @returns 判定関数
   */
  const getValidateFunctionForRecommendOrder = (index: number) => {
    return _isDisabledDateForRecommendOrder(getRecommendCrossDate(index));
  };

  /**
   * TODO: corePageModule/service/datepickerService.ts に移動する
   * 推奨発注日のカレンダーで選択できる日付かどうかを判定する
   * @param recommendCrossDate - 推奨納入日
   * @returns カレンダーで選択できる日付かどうか
   */
  const _isDisabledDateForRecommendOrder = (recommendCrossDate: Date | undefined) => {
    // date - 推奨発注日として選択しようとしている日付
    return function (date: Date): boolean {
      // 推奨納入日が未設定の場合は、制限なく選択可能
      if (!recommendCrossDate) return false;
      // 時間を無視
      recommendCrossDate.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      // 推奨発注日は、納入日以前の日付である必要がある
      return date > recommendCrossDate;
    };
  };

  /**
   * 推奨納入日のカレンダーで選択できる日付かどうかを判定する関数を取得する
   * @param index - 判定対象の発注推奨のインデックス
   * @returns 判定関数
   */
  const getValidateFunctionForRecommendCross = (index: number) => {
    return _isDisabledDateForRecommendCross(getRecommendOrderDate(index));
  };

  /**
   * TODO: corePageModule/service/datepickerService.ts に移動する
   * 推奨納入日のカレンダーで選択できる日付かどうかを判定する
   * @param recommendOrderDate - 推奨発注日
   * @returns カレンダーで選択できる日付かどうか
   */
  const _isDisabledDateForRecommendCross = (recommendOrderDate: Date | undefined) => {
    // date - 推奨納入日として選択しようとしている日付
    return function (date: Date): boolean {
      const today = new Date();
      // 時間を無視
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      const beforeToday = date < today;
      // 推奨発注日が未設定の場合は、今日以降の日付であれば選択可能
      if (!recommendOrderDate) return beforeToday;
      recommendOrderDate.setHours(0, 0, 0, 0);
      // 推奨納入日は、発注日以降の日付 && 今日以降の日付 である必要がある
      return date < recommendOrderDate || beforeToday;
    };
  };

  /**
   * 登録された発注IDをローカルの発注に反映する
   * @param registeredOrderIds - 登録された発注ID
   */
  const _updateOrderIdForRegisteredOrder = (registeredOrderIds: BulkPostOrderResponseInfoRegisteredOrderIdsInner[]) => {
    registeredOrderIds.forEach((registeredOrderId) => {
      state.recommend.forEach((recommendOrderForm) => {
        if (recommendOrderForm.displayId === registeredOrderId.displayId) {
          recommendOrderForm.arrivalId = registeredOrderId.orderId;
          recommendOrderForm.isFixOrdered = true; // DBに登録された時点でtrueになる
        }
      });
    });
  };

  /**
   * 全ての値を初期化する
   */
  const initializeAll = async () => {
    const result = await openConfirmModal(
      'question',
      t('すべての値を初期化(詳細ページ)'),
      t('確定'),
      t('もどる'),
      undefined,
      t('初期化テキスト')
    );
    if (!result.isConfirmed) return;
    resetAllSimulateState();
  };

  /**
   * リードタイムの値を変更する
   * @param value - リードタイムの値
   */
  const changedLeadTimeDisplayValue = (value: number) => {
    state.selectedLeadTimeDisplayValue = value;
  };

  /**
   * リードタイムの単位を変更する
   * @param unit - 現在のリードタイムの単位
   */
  const changedLeadTimeUnit = (unit: string) => {
    state.selectedLeadTimeUnit = unit;
  };

  /**
   * デフォルトの客先内示が空であれば更新する
   * @param announce - デフォルトの客先内示データ
   */
  const updateDefaultCustomerAnnounceIfNeeded = (announce: IAnnounce[]) => {
    if (state.defaultCustomerAnnounce.length === 0) {
      state.defaultCustomerAnnounce = announce;
    }
  };

  /**
   * 指定されたインデックスのアイテムがチェックされているか、またはロックされているかを判定します。
   * @param index - アイテムのインデックス。
   * @param item - チェックするアイテムの配列。
   * @returns アイテムがチェックされているかロックされている場合は true を返し、それ以外は false を返します。
   */
  const isItemCheckedOrLocked = (index: number, item: any): boolean => {
    return state.checkedIndex.includes(item[index]) || (state.recommend[index]?.isLocked ?? false);
  };

  /**
   * 指定されたインデックスの発注より納期が前の未登録の推奨発注を削除する
   * ステータスを変更した発注が既存発注の次に並ぶようにするため、間にある推奨発注は承認依頼にせず取り除く
   * @param index - ステータスを更新する推奨発注のインデックス
   * @param recommendOrderForms - 削除対象を含む推奨発注
   * @returns 削除後の推奨発注と、削除した推奨発注の元のインデックス
   */
  const removeRecommendedOrdersBeforeIndex = (
    index: number,
    recommendOrderForms: IRecommendOrderArrivalForRecommendEdit[]
  ): { updatedForms: IRecommendOrderArrivalForRecommendEdit[]; removedIndices: number[] } => {
    const removedIndices: number[] = [];
    const eta = recommendOrderForms[index].crossDate;
    if (!eta) {
      throw new Error();
    }
    const updatedForms = recommendOrderForms.filter((item, i) => {
      if (i === index || !item.crossDate) return true;
      // 未登録（arrivalIdなし）かつ推奨のままで、納期が変更対象と同じか前のものだけを削除する
      const isRecommended = (item.status ?? OrderStatus.Recommended) === OrderStatus.Recommended;
      if (item.arrivalId === '' && isRecommended && DateService.compareDates(eta, item.crossDate) !== -1) {
        removedIndices.push(i);
        return false;
      }
      return true;
    });

    return { updatedForms, removedIndices };
  };

  /**
   * 指定されたインデックスの発注推奨をまとめて削除する
   * @param indices - 削除するrecommendのindex
   */
  const removeRecommendByIndices = (indices: number[]) => {
    if (indices.length === 0) return;
    state.recommend = state.recommend.filter((_, i) => !indices.includes(i));
  };

  /**
   * シミュレーション開始日を計算する
   * @param lockedRecommendLast - 更新に使用する推奨発注データ
   */
  const calculateStartDate = (lockedRecommendLast?: IRecommendOrderArrivalForRecommendEdit): Date | undefined => {
    // ロックされている要素がある場合のみ、シミュレーションを更新する
    if (!lockedRecommendLast?.crossDate) return undefined;
    // lockedRecommendLastのcrossDateを取得し、Date型に
    const lockedRecommendLastCrossDate = DateService.convertIDateToDate(lockedRecommendLast.crossDate);
    // lockedRecommendLastCrossDateの1日前の変数lockedRecommendLastCrossDateBeforeを作成
    //　昨日の日付を取得する(new Data()の引数には、年月日を入れる
    const lockedRecommendLastCrossDateBefore = new Date(
      lockedRecommendLastCrossDate.getFullYear(),
      lockedRecommendLastCrossDate.getMonth(),
      lockedRecommendLastCrossDate.getDate() + 1
    );
    return lockedRecommendLastCrossDateBefore;
  };

  /**
   * 選択された情報を更新する
   * 空文字はフィルター条件から外す
   * @param warehouseName - 倉庫名
   * @param supplierName  - 仕入先名
   * @param businessCode - 商品コード
   * @param businessName - 商品名
   * @param packageAmount - 容量
   */
  const updateSelectedInfo = (
    warehouseName?: string,
    supplierName?: string,
    businessCode?: string,
    businessName?: string,
    packageAmount?: string
  ) => {
    if (warehouseName || warehouseName == '') state.selectedInfo.warehouse = warehouseName;
    if (supplierName || supplierName == '') state.selectedInfo.supplierName = supplierName;
    if (businessCode || businessCode == '') state.selectedInfo.businessCode = businessCode;
    if (businessName || businessName == '') state.selectedInfo.businessName = businessName;
    if (packageAmount || packageAmount == '') state.selectedInfo.packageAmount = packageAmount;
  };

  /**
   * リードタイムのデフォルト値の更新
   * @param leadTimeValue - リードタイムの値
   * @param leadTimeUnit - リードタイムの単位
   */
  const onUpdateDefaultLeadTime = async (leadTimeValue: number, leadTimeUnit: DateUnitType) => {
    // モーダル確認モーダルを表示
    const result = await openConfirmModal(
      'question',
      t('デフォルト値の更新'),
      t('確定'),
      t('キャンセル'),
      undefined,
      t('この取引のリードタイムに初期表示する情報を更新しますか？')
    );
    if (!result.isConfirmed) return;

    // ローディング開始
    state.loadingState.status.type = 'loading';

    // デフォルト値の更新処理
    const updateResult = await diContainer.putLeadTimeUseCase.Execute(bizId, leadTimeValue, leadTimeUnit);

    switch (updateResult.status.type) {
      case 'success':
        // 完了モーダルを表示する
        state.loadingState.status.type = 'success';
        await openSuccessToSaveModal();
        // TODO: 表示値を更新するかどうか確認
        break;

      case 'error':
        // エラーモーダルを表示する
        console.error(updateResult.status.error);
        state.loadingState.status.type = 'error';
        await openOkInformationModal(
          'error',
          t('更新に失敗しました'),
          t('閉じる'),
          undefined,
          t('リードタイムの初期表示情報の更新に失敗しました。')
        );
        return;
    }
  };

  /**
   * 標準在庫指数のデフォルト値の更新
   * @param stdInventoryIndices - 標準在庫指数
   */
  const onUpdateDefaultStdInventoryIndices = async (stdInventoryIndices: StdIndexInfo) => {
    // モーダル確認モーダルを表示
    const result = await openConfirmModal(
      'question',
      t('デフォルト値の更新'),
      t('確定'),
      t('キャンセル'),
      undefined,
      t('この取引の標準在庫指数に初期表示する情報を更新しますか？')
    );
    if (!result.isConfirmed) return;

    // ローディング開始
    state.loadingState.status.type = 'loading';

    // デフォルト値の更新処理
    const updateResult = await diContainer.putStdInventoryIndicesUseCase.Execute(bizId, stdInventoryIndices);

    switch (updateResult.status.type) {
      case 'success':
        // 完了モーダルを表示する
        state.loadingState.status.type = 'success';
        await openSuccessToSaveModal();
        // TODO: 表示値を更新するかどうか確認
        break;

      case 'error':
        // エラーモーダルを表示する
        console.error(updateResult.status.error);
        state.loadingState.status.type = 'error';
        await openOkInformationModal(
          'error',
          t('更新に失敗しました'),
          t('閉じる'),
          undefined,
          t('標準在庫指数の初期表示情報の更新に失敗しました。')
        );
        return;
    }
  };

  /**
   * MOQの更新
   * @param moq - MOQ
   */
  const onUpdateMoq = async (moq: number) => {
    // モーダル確認モーダルを表示
    const result = await openConfirmModal(
      'question',
      t('MOQの更新'),
      t('確定'),
      t('キャンセル'),
      undefined,
      t('MOQを更新しますか？（更新すると全ての値・シミュレーションが初期化されます。）')
    );
    if (!result.isConfirmed) return false;

    // ローディング開始
    state.loadingState.status.type = 'loading';

    // 更新処理
    const updateResult = await diContainer.putMoqUseCase.Execute(bizId, moq);

    switch (updateResult.status.type) {
      case 'success':
        // 完了モーダルを表示する
        state.loadingState.status.type = 'success';
        await openSuccessToSaveModal();
        await resetAllSimulateState();
        return true;
      case 'error':
        // エラーモーダルを表示する
        console.error(updateResult.status.error);
        state.loadingState.status.type = 'error';
        await openOkInformationModal(
          'error',
          t('更新に失敗しました'),
          t('閉じる'),
          undefined,
          t('MOQの更新に失敗しました。時間をおいて再度お試しください。')
        );
        return false;
    }
  };

  /**
   * 発注単位の更新
   * @param orderPackage - 発注単位
   */
  const onUpdateOrderPackage = async (orderPackage: number) => {
    // モーダル確認モーダルを表示
    const result = await openConfirmModal(
      'question',
      t('発注単位の更新'),
      t('確定'),
      t('キャンセル'),
      undefined,
      t('発注単位を更新しますか？（更新すると全ての値・シミュレーションが初期化されます。）')
    );
    if (!result.isConfirmed) return false;

    // ローディング開始
    state.loadingState.status.type = 'loading';

    // 更新処理
    const updateResult = await diContainer.putOrderPackageUseCase.Execute(bizId, orderPackage);

    switch (updateResult.status.type) {
      case 'success':
        // 完了モーダルを表示する
        state.loadingState.status.type = 'success';
        await openSuccessToSaveModal();
        await resetAllSimulateState();
        return true;
      case 'error':
        // エラーモーダルを表示する
        console.error(updateResult.status.error);
        state.loadingState.status.type = 'error';
        await openOkInformationModal(
          'error',
          t('更新に失敗しました'),
          t('閉じる'),
          undefined,
          t('発注単位の更新に失敗しました。時間をおいて再度お試しください。')
        );
        return false;
    }
  };

  /**
   * メモを更新する
   * @param memoText - メモの文章
   */
  const updateMemo = async (memoText: string): Promise<void> => {
    // モーダル確認モーダルを表示
    const result = await openConfirmModal(
      'question',
      t('メモの更新'),
      t('確定'),
      t('キャンセル'),
      undefined,
      t('メモを更新しますか？（更新すると全ての値・シミュレーションが初期化されます。）')
    );
    if (!result.isConfirmed) return;

    // ローディング開始
    state.loadingState.status.type = 'loading';

    // 更新処理
    const updateResult = await diContainer.putAggregateBusinessMemoUseCase.Execute(bizId, memoText);

    switch (updateResult.status.type) {
      case 'success':
        // 発注に紐づくリマインドメール本文も更新（失敗は握りつぶす）
        try {
          // 現在のリマインダー一覧を取得
          const reminderList: ReminderMailInfo[] = await diContainer.getReminderDataUseCase.execute();

          // 詳細ページ内の発注群から、reminderMailDataIdを抽出（重複除去）
          const targetReminderIds = Array.from(
            new Set(state.recommend.map((r) => r.reminderMailDataId).filter((v): v is string => typeof v === 'string' && v.length > 0))
          );

          for (const dataId of targetReminderIds) {
            const info = reminderList.find((r) => r.dataId === dataId);
            if (info) {
              await updateReminder(info.dataId, info.date, info.mailTo, info.mailFrom, info.title, memoText);
            }
          }
        } catch (e) {
          // 失敗してもUIにはエラーを出さない
          console.error('reminder mail body update failed on detail page (ignored):', e);
        }

        // 完了モーダルを表示する
        state.loadingState.status.type = 'success';
        await openSuccessToSaveModal();
        await resetAllSimulateState();
        return;
      case 'error':
        // エラーモーダルを表示する
        console.error(updateResult.status.error);
        state.loadingState.status.type = 'error';
        await openOkInformationModal(
          'error',
          t('更新に失敗しました'),
          t('閉じる'),
          undefined,
          t('メモの更新に失敗しました。時間をおいて再度お試しください。')
        );
        return;
    }
  };

  /**
   * メモを削除する
   */
  const deleteMemo = async () => {
    // モーダル確認モーダルを表示
    const result = await openConfirmModal(
      'question',
      t('メモの削除'),
      t('確定'),
      t('キャンセル'),
      undefined,
      t('メモを削除しますか？（メモを削除すると全ての値・シミュレーションが初期化されます。）')
    );
    if (!result.isConfirmed) return;

    // ローディング開始
    state.loadingState.status.type = 'loading';

    // 更新処理
    const updateResult = await diContainer.deleteAggregateBusinessMemoUseCase.Execute(bizId);

    switch (updateResult.status.type) {
      case 'success':
        // 完了モーダルを表示する
        state.loadingState.status.type = 'success';
        await openOkInformationModal('success', t('成功'), t('OK'), t('メモを削除しました。'));
        await resetAllSimulateState();
        return;
      case 'error':
        // エラーモーダルを表示する
        console.error(updateResult.status.error);
        state.loadingState.status.type = 'error';
        await openOkInformationModal(
          'error',
          t('更新に失敗しました'),
          t('閉じる'),
          undefined,
          t('メモの削除に失敗しました。時間をおいて再度お試しください。')
        );
        return;
    }
  };

  /**
   * 推奨発注内のメモの値を更新する
   * @param index -index
   * @param memoText -memoText
   */
  const updateRecommendMemoValue = (index: number, memoText: string) => {
    state.recommend[index].memoText = memoText;
  };

  /**
   * 分納情報の取得・展開を行う
   * @param index - 分納情報を展開する推奨発注のインデックス
   */
  const expandPartialDelivery = (index: number) => {
    // 表示情報の展開フラグを更新
    state.recommend[index].isExpanded = true;

    // 分納情報の取得
    const getPartialDeliveryResult = state.recommend[index].partialDeliveries;
    state.partialDeliveryForms = getPartialDeliveryResult ? simpleCopy(getPartialDeliveryResult) : [];
    state.renderRecommendEditKey += 1;
  };

  /**
   * 分納情報を閉じる処理を行う
   * @param index - 分納情報を閉じる推奨発注のインデックス
   */
  const closePartialDelivery = (index: number) => {
    state.recommend[index].isExpanded = false;
    state.renderRecommendEditKey += 1;
  };

  /**
   * 最終チェック情報を更新する
   */
  const updateLastChecked = async () => {
    const updateResult = await diContainer.putLastCheckedUseCase.execute(state.formSet.businessId);

    switch (updateResult.status.type) {
      case 'success':
        toast.success('最終チェック日時を更新しました');
        state.formSet.lastCheckedUser = updateResult.status.data.lastCheckedUser;
        state.formSet.lastCheckedAt = updateResult.status.data.lastCheckedAt;
        return;
      case 'error':
      default:
        toast.error('最終チェック日時の更新に失敗しました');
        return;
    }
  };

  /**
   * 編集した発注済み未納で発注を更新する
   * @param editedOrder - 編集した発注済み未納一覧
   */
  const updateUnpaidOrderedOrder = async (editedOrder: IArrival[]) => {
    const result = await waitLoading(diContainer.bulkPutUnpaidOrderedOrderUseCase.execute(editedOrder));
    switch (result?.status.type) {
      case 'success':
        const resultData = result.status.data;
        if (resultData.notUpdatedOrderId.length > 0) {
          console.error('発注済み未納一括編集一部競合');
          await showInfoModal({
            modalTitle: t('一部の発注済み未納の編集に失敗しました'),
            modalDescription: t('一部の発注済み未納の編集に失敗しました。時間をおいて再度お試しください。'),
            backButtonLabel: t('OK'),
            isDisabledNextButton: true,
            enableOutsideClick: false,
          });
        } else {
          await showInfoModal({
            modalTitle: t('発注済み未納の編集に成功しました'),
            modalDescription: '',
            backButtonLabel: t('OK'),
            isDisabledNextButton: true,
            enableOutsideClick: false,
          });
        }
        state.isOpeningEditUnpaidOrderedOrderModal = false;
        await waitLoading(resetAllSimulateState());
        return;
      case 'error':
      default:
        console.error('発注済み未納一括編集に失敗');
        await showInfoModal({
          modalTitle: t('発注済み未納の編集に失敗しました'),
          modalDescription: t('発注済み未納の編集に失敗しました。リロードして再度お試しください。'),
          backButtonLabel: t('OK'),
          isDisabledNextButton: true,
          enableOutsideClick: false,
        });
    }
  };

  /**
   * 関連取引データを取得する
   * @param aggregateBusinessId - 集計取引ID
   * @param receiptMonthOffset - 受領月オフセット（Nヶ月前内示を指定する）
   */
  const fetchRelatedTransactionsData = async (aggregateBusinessId: string, receiptMonthOffset: number) => {
    state.relatedTransactionsLoading = true;
    state.relatedTransactionsError = null;

    try {
      const networkResult = await diContainer.getRelatedTransactionsDetailUseCase.execute(aggregateBusinessId, receiptMonthOffset);

      if (networkResult.status.type === 'success') {
        state.relatedTransactionsData = networkResult.status.data;
        state.relatedTransactionsLoading = false;
      } else {
        throw new Error(networkResult.status.error?.message || 'データの取得に失敗しました');
      }
    } catch (error) {
      console.error('Failed to fetch related transactions:', error);
      state.relatedTransactionsError = error instanceof Error ? error.message : 'データの取得に失敗しました';
      state.relatedTransactionsLoading = false;
    }
  };

  /**
   * 関連取引モーダルを開く
   * @param aggregateBusinessId - 集計取引ID
   */
  const openRelatedTransactionsModal = async (aggregateBusinessId: string) => {
    state.relatedTransactionsModalState = true;
    state.relatedTransactionsData = null;
    state.relatedTransactionsReceiptMonthOffset = 1; // デフォルト値を1に設定
    await fetchRelatedTransactionsData(aggregateBusinessId, state.relatedTransactionsReceiptMonthOffset);
  };

  /**
   * 関連取引モーダルを閉じる
   */
  const closeRelatedTransactionsModal = () => {
    state.relatedTransactionsModalState = false;
    state.relatedTransactionsData = null;
    state.relatedTransactionsError = null;
  };

  /**
   * 関連取引モーダルの受領月オフセットを更新する
   * @param aggregateBusinessId - 集計取引ID
   * @param value - 選択された値（例: "1ヶ月前内示" または "1 month ago forecast"）
   */
  const updateRelatedTransactionsReceiptMonthOffset = async (aggregateBusinessId: string, value: string) => {
    // 数値を抽出（先頭の数字を取得）
    const match = value.match(/^(\d+)/);
    const offset = match ? Number(match[1]) : 1;
    state.relatedTransactionsReceiptMonthOffset = offset;
    await fetchRelatedTransactionsData(aggregateBusinessId, offset);
  };

  return {
    state,
    updateSimulate: updateSimulate,
    updateExtraSimulate: updateExtraSimulate,
    updateRecommend: updateRecommend,
    updateExtraRecommend: updateExtraRecommend,
    updateSupplierAnnounce: updateExtraSupplierAnnounce,
    lockRecommend: lockRecommend,
    unLockRecommend: unlockRecommend,
    addRecommend: addRecommend,
    trashRecommend: trashRecommend,
    confirmReceiveAnnounce: confirmReceiveAnnounce,
    reviseCustomerAnnounce: reviseCustomerAnnounce,
    confirmRecommend: confirmRecommend,
    confirmSupplierAnnounce: confirmSupplierAnnounce,
    reviseRecommend: reviseRecommend,
    resetExtraSimulation: resetExtraSimulation,
    saveConfirmOrderInfo: saveConfirmOrderInfo,
    saveConfirmSupplierAnnounce: saveConfirmSupplierAnnounce,
    initState: resetSimulationInfo,
    initUiState: _initUiState,
    calcAnnounceAdjustment: calcAnnounceAdjustment,
    getRecommendOrderDate: getRecommendOrderDate,
    getRecommendCrossDate: getRecommendCrossDate,
    getPartialDeliveryCrossDate: getPartialDeliveryCrossDate,
    getRecommendOrderNote: getRecommendOrderNote,
    getValidateFunctionForRecommendOrder: getValidateFunctionForRecommendOrder,
    getValidateFunctionForRecommendCross: getValidateFunctionForRecommendCross,
    initializeAll: initializeAll,
    changedLeadTimeDisplayValue: changedLeadTimeDisplayValue,
    changedLeadTimeUnit: changedLeadTimeUnit,
    updateDefaultCustomerAnnounceIfNeeded: updateDefaultCustomerAnnounceIfNeeded,
    updateBizId: updateBizId,
    updateAnnounceAdjustmentRate: updateAnnounceAdjustmentRate,
    isItemCheckedOrLocked: isItemCheckedOrLocked,
    removeRecommendedOrdersBeforeIndex: removeRecommendedOrdersBeforeIndex,
    removeRecommendByIndices: removeRecommendByIndices,
    updateSelectedInfo: updateSelectedInfo,
    calculateStartDate: calculateStartDate,
    onUpdateDefaultLeadTime: onUpdateDefaultLeadTime,
    onUpdateDefaultStdInventoryIndices: onUpdateDefaultStdInventoryIndices,
    onUpdateMoq: onUpdateMoq,
    onUpdateOrderPackage: onUpdateOrderPackage,
    updateMemo: updateMemo,
    deleteMemo: deleteMemo,
    updateRecommendMemoValue: updateRecommendMemoValue,
    resetOnlyRecommendOrder: resetOnlyRecommendOrder,
    expandPartialDelivery: expandPartialDelivery,
    closePartialDelivery: closePartialDelivery,
    updateLastChecked: updateLastChecked,
    openUnpaidOrderedOrderInfoModal: openUnpaidOrderedOrderInfoModal,
    updateUnpaidOrderedOrder: updateUnpaidOrderedOrder,
    openRelatedTransactionsModal: openRelatedTransactionsModal,
    closeRelatedTransactionsModal: closeRelatedTransactionsModal,
    updateRelatedTransactionsReceiptMonthOffset: updateRelatedTransactionsReceiptMonthOffset,
  };
};

/**
 * このファイル内で定義されている関数を呼び出すためのインターフェース
 */
interface IRecommendOrderStore {
  /** システムの状態 */
  readonly state: IBusinessDetail;

  /**
   * シミュレーションを更新する
   * @param recommend - 更新に使用する入荷データ
   */
  updateSimulate: (recommend: IRecommendOrderArrival[]) => void;

  /**
   * シミュレーションを更新する（拡張機能）
   * @param customerAnnounce - 顧客からのアナウンス
   * @param stdInventoryIndices - 標準在庫指数
   * @param leadTime - リードタイム
   * @param recommend - 更新に使用する推奨発注データ
   * @param startDate - 開始日
   */
  updateExtraSimulate: (
    customerAnnounce?: IAnnounce[],
    stdInventoryIndices?: StdInventoryIndex,
    leadTime?: number,
    recommend?: IRecommendOrderArrival[],
    startDate?: Date
  ) => void;

  /** 初期化する */
  initState: () => void;

  /** uiStateを初期化する */
  initUiState: () => void;

  /**
   * OrderForm -> RecommendEditへのデータの受け渡し
   * @param recommend - 発注推奨データ（編集用）
   * @param customerAnnounce - 顧客からのアナウンス
   */
  updateRecommend: (recommend: IRecommendOrderArrivalForRecommendEdit[], customerAnnounce: IAnnounce[]) => void;

  /**
   * 発注推奨（編集用）を更新する
   * @param recommend - 発注推奨データ（編集用）
   */
  updateExtraRecommend: (recommend: IRecommendOrderArrivalForRecommendEdit[]) => void;

  /**
   * 提示内示を更新する
   * @param announce - 内示データ
   */
  updateSupplierAnnounce: (announce: IAnnounce[]) => void;

  /**
   * 発注推奨にロックをかける
   * @param index - ロックをかける発注推奨のインデックス
   */
  lockRecommend: (index: number) => void;

  /**
   * 発注推奨のロックを解除する
   * @param index - ロックを解除する発注推奨のインデックス
   */
  unLockRecommend: (index: number) => void;

  /**
   * 発注推奨を追加する
   * @param recommend - 追加する発注推奨
   * @returns 追加された発注推奨
   */
  addRecommend: (recommend?: IRecommendOrderArrivalForRecommendEdit) => IRecommendOrderArrivalForRecommendEdit;

  /**
   * 発注推奨を削除する
   * @param index - 削除する発注推奨のインデックス
   */
  trashRecommend: (index: number) => void;

  /** 客先内示を確定する */
  confirmReceiveAnnounce: () => void;

  /** 客先内示を修正する */
  reviseCustomerAnnounce: () => void;

  /** 発注推奨を確定する */
  confirmRecommend: (checkedRecommends: IRecommendOrderArrivalForRecommendEdit[]) => Promise<INetworkResult<void, Error>>;

  /**
   * 仕入先内示を確定する
   * @param registerSupplierForecastRange - 登録する仕入先内示の期間
   * @param useCustomerAnnounce - 客先内示を仕入先内示とするか
   * @param selectedYearMonth - 選択年月
   */
  confirmSupplierAnnounce: (registerSupplierForecastRange: number, useCustomerAnnounce: boolean, selectedYearMonth?: IYearMonth) => void;

  /** 発注推奨を修正する */
  reviseRecommend: () => void;

  /** 発注推奨シミュレーションをリセットする */
  resetExtraSimulation: () => void;

  /**
   * 確定発注を保存する
   * @param checkedRecommends - 保存する確定発注のインデックス
   */
  saveConfirmOrderInfo: (checkedRecommends: IRecommendOrderArrivalForRecommendEdit[]) => Promise<INetworkResult<void, Error>>;

  /**
   * 提示内示を保存する
   * @param saveRecommendIndexes - 保存する確定発注のインデックス
   */
  saveConfirmSupplierAnnounce: (registerSupplierForecastRange: number, useCustomerAnnounce: boolean, createdYearMonth?: IYearMonth) => void;

  /**
   * 全ての値を初期化する
   */
  initializeAll: () => void;

  /**
   * リードタイムの値を変更する
   * @param value - リードタイムの値
   */
  changedLeadTimeDisplayValue: (value: number) => void;

  /**
   * リードタイムの単位を変更する
   */
  changedLeadTimeUnit: (unit: string) => void;

  /**
   * 内示調整を行う
   * @param announce - 内示データ
   */
  calcAnnounceAdjustment: (announce: IAnnounce[]) => void;

  /**
   * デフォルトの客先内示が空であれば更新する
   */
  updateDefaultCustomerAnnounceIfNeeded: (announce: IAnnounce[]) => void;

  /**
   * bizIdを更新する
   * @param businessId - 取引ID
   */
  updateBizId: (businessId: string) => void;

  /**
   * 内示調整の倍率を更新する
   * @param rate - 内示調整の倍率
   */
  updateAnnounceAdjustmentRate: (rate: number) => void;

  /**
   * 推奨発注日を取得する
   * @param index - 推奨発注のインデックス
   * @returns 推奨発注日
   */
  getRecommendOrderDate: (index: number) => Date | undefined;

  /**
   * 推奨納入日を取得する
   * @param index - 推奨発注のインデックス
   * @returns 推奨納入日
   */
  getRecommendCrossDate: (index: number) => Date | undefined;

  /**
   * 分納情報の納入日を取得する
   * @param recommendIndex - 推奨発注のインデックス
   * @param partialDeliveryIndex - 分納情報のインデックス
   * @returns 分納情報の納入日
   */
  getPartialDeliveryCrossDate: (recommendIndex: number, partialDeliveryIndex: number) => Date | undefined;

  /**
   * 推奨発注のメモの表示用の文字列を取得する
   * @param index - 推奨発注のインデックス
   */
  getRecommendOrderNote: (index: number) => string | undefined;

  /**
   * 推奨発注日のカレンダーで選択できる日付かどうかを判定する関数を取得する
   * @param index - 判定対象の発注推奨のインデックス
   * @returns 判定関数
   */
  getValidateFunctionForRecommendOrder: (index: number) => (date: Date) => boolean;

  /**
   * 推奨納入日のカレンダーで選択できる日付かどうかを判定する関数を取得する
   * @param index - 判定対象の発注推奨のインデックス
   * @returns 判定関数
   */
  getValidateFunctionForRecommendCross: (index: number) => (date: Date) => boolean;

  /**
   * 指定されたインデックスのアイテムがチェックされているか、またはロックされているかを判定します。
   * @param index - アイテムのインデックス。
   * @param item - チェックするアイテムの配列。
   * @returns アイテムがチェックされているかロックされている場合は true を返し、それ以外は false を返します。
   */
  isItemCheckedOrLocked: (index: number, item: any) => boolean;

  /**
   * 指定されたインデックスの発注より納期が前の未登録の推奨発注を削除する
   * @param index - ステータスを更新する推奨発注のインデックス
   * @param recommendOrderForms - 削除対象を含む推奨発注
   * @returns 削除後の推奨発注と、削除した推奨発注の元のインデックス
   */
  removeRecommendedOrdersBeforeIndex: (
    index: number,
    recommendOrderForms: IRecommendOrderArrivalForRecommendEdit[]
  ) => { updatedForms: IRecommendOrderArrivalForRecommendEdit[]; removedIndices: number[] };

  /**
   * 指定されたインデックスの発注推奨をまとめて削除する
   * @param indices - 削除するrecommendのindex
   */
  removeRecommendByIndices: (indices: number[]) => void;

  /**
   * 選択された情報を更新する
   * @param warehouseName - 倉庫名
   * @param supplierName  - 仕入先名
   * @param businessCode - 商品コード
   * @param businessName - 商品名
   * @param packageAmount - 容量
   */
  updateSelectedInfo: (
    warehouseName?: string,
    supplierName?: string,
    businessCode?: string,
    businessName?: string,
    packageAmount?: string
  ) => void;

  /**
   * シミュレーション開始日を計算する
   * @param recommendOrderForms - 更新に使用する推奨発注データ
   */
  calculateStartDate: (recommendOrderForms?: IRecommendOrderArrivalForRecommendEdit) => Date | undefined;

  /**
   * リードタイムのデフォルト値の更新
   * @param leadTimeValue - リードタイムの値
   * @param leadTimeUnit - リードタイムの単位
   */
  onUpdateDefaultLeadTime: (leadTimeValue: number, leadTimeUnit: DateUnitType) => void;

  /**
   * 標準在庫指数のデフォルト値の更新
   * @param stdInventoryIndices - 標準在庫指数
   */
  onUpdateDefaultStdInventoryIndices: (stdInventoryIndices: StdIndexInfo) => void;

  /**
   * MOQの更新
   * @param moq - MOQ
   */
  onUpdateMoq: (moq: number) => Promise<boolean>;

  /**
   * 発注パッケージの更新
   * @param orderPackage - 発注パッケージの数
   */
  onUpdateOrderPackage: (orderPackage: number) => Promise<boolean>;

  /**
   * メモの更新
   * @param memoText - メモのテキスト
   */
  updateMemo: (memoText: string) => Promise<void>;

  /**
   * メモの削除
   */
  deleteMemo: () => Promise<void>;

  /**
   * 推奨発注内のメモの値を更新する
   * @param index -index
   * @param memoText -memoText
   */
  updateRecommendMemoValue: (index: number, memoText: string) => void;

  resetOnlyRecommendOrder: () => void;

  /**
   * 分納情報の展開を行う
   * @param index - 分納情報を展開する推奨発注のインデックス
   */
  expandPartialDelivery: (index: number) => void;

  /**
   * 分納情報の閉じる処理を行う
   * @param index - 分納情報を閉じる推奨発注のインデックス
   */
  closePartialDelivery: (index: number) => void;

  /**
   * 最終チェック情報を更新する
   */
  updateLastChecked: () => void;
  openUnpaidOrderedOrderInfoModal: (isProcessingConfirm?: boolean) => Promise<boolean>;

  updateUnpaidOrderedOrder: (editedOrder: IArrival[]) => Promise<void>;

  /**
   * 関連取引モーダルを開く
   * @param aggregateBusinessId - 集計取引ID
   */
  openRelatedTransactionsModal: (aggregateBusinessId: string) => Promise<void>;

  /**
   * 関連取引モーダルを閉じる
   */
  closeRelatedTransactionsModal: () => void;

  /**
   * 関連取引モーダルの受領月オフセットを更新する
   * @param aggregateBusinessId - 集計取引ID
   * @param value - 選択された値（例: "1ヶ月前内示" または "1 month ago forecast"）
   */
  updateRelatedTransactionsReceiptMonthOffset: (aggregateBusinessId: string, value: string) => Promise<void>;
}

export const key: InjectionKey<IRecommendOrderStore> = Symbol('key');
